import { useEffect, useState, lazy, Suspense } from 'react';
import { useDispatch } from 'react-redux'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { ErrorBoundary } from "react-error-boundary";

import { getEnvConfig } from 'kmstack-shared/core';
import { initApiClient } from 'kmstack-shared/services';
import { setupSpinner, Spinner } from 'kmstack-shared/components/basic';

import { ProtectedRoute } from './routes/ProtectedRoute'
import { PublicRoute } from './routes/PublicRoute'

// Lazy load route components for code splitting
const Signup = lazy(() => import('./pages/Signup'));
const LinkAccount = lazy(() => import('./pages/LinkAccount'));
const RedirectAfterLogin = lazy(() => import('./pages/RedirectAfterLogin'));
const RedirectOnLogin = lazy(() => import('./pages/RedirectOnLogin'));
const AccountAccessRouter = lazy(() => import('./features/account/components/AccountAccessRouter'));
const Login = lazy(() => import('./features/auth/components/Login'));
const Reset = lazy(() => import('./features/auth/components/Reset'));
const DeactivateAccount = lazy(() => import('./features/account/security/DeactivateAccount'));
const DeleteAccount = lazy(() => import('./features/account/security/DeleteAccount'));

import { cn } from './utils/utils.types'

import { ShortcutsHelpModal } from './components/modals/ShortcutsHelpModal';
import { GlobalErrorFallback } from './components/error/GlobalErrorFallback';
import { InactivityProvider } from 'kmstack-shared/providers';
import { AuthService } from './features/auth';
import { store } from './utils/store';
import { ActionBanner } from './components/ActionBanner';
import { useKeyboardShortcuts } from 'kmstack-shared/hooks';

const Home = () => <Navigate to="/login" replace />
const isProd = import.meta.env.PROD

// Wrapper component to initialize API client with dispatch
function ApiClientInitializer({ children }: { children: React.ReactNode }) {
    const dispatch = useDispatch();
    const [isInitialized, setIsInitialized] = useState(false);

    useEffect(() => {
        const envConfig = getEnvConfig()
        initApiClient({
            mode: isProd ? 'prod' : 'local',
            apiUrl: envConfig.apiUrl,
            loginUrl: '/login',
            authPages: ['/login', '/signup', '/reset-password'],
            allowedHosts: envConfig.allowedHosts,
            dispatch
        });
        setupSpinner(cn)
        setIsInitialized(true);
    }, [dispatch]);

    return <>{isInitialized ? children : null}</>;
}



function App() {
    const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);
    const [showInactivityWarning, setShowInactivityWarning] = useState(false);

    useKeyboardShortcuts([
        {
            combo: { key: '/', ctrlKey: true },
            handler: () => setIsShortcutsHelpOpen(true),
            description: 'Open shortcuts help',
        },
        {
            combo: { key: '/', metaKey: true }, // Cmd+/ for Mac
            handler: () => setIsShortcutsHelpOpen(true),
            description: 'Open shortcuts help',
        }
    ]);

    const shortcutsList = [
        { keys: ['Ctrl', '/'], description: 'Open shortcuts help' },
        { keys: ['⌘', '/'], description: 'Open shortcuts help (Mac)' },
        { keys: ['Esc'], description: 'Close modals / menus' },
        { keys: ['Tab'], description: 'Navigate forward' },
        { keys: ['Shift', 'Tab'], description: 'Navigate backward' },
        { keys: ['Space'], description: 'Activate buttons' },
        { keys: ['Enter'], description: 'Submit forms / Activate links' },
    ];

    const handleInactivityWarning = () => {
        setShowInactivityWarning(true);
        // User requested 55 seconds
        setTimeout(() => setShowInactivityWarning(false), 55000);
    };

    return (
        <ApiClientInitializer>
            <InactivityProvider
                timeout={30}
                warningTimeout={1}
                onTimeout={() => AuthService.logout(store.dispatch)}
                onWarning={handleInactivityWarning}
            >
                {/* Skip navigation link for accessibility */}
                <a href="#main-content" className="skip-link">
                    Skip to main content
                </a>

                {showInactivityWarning && (
                    <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-[100] w-full max-w-md px-4 pointer-events-none">
                        <div className="pointer-events-auto shadow-2xl">
                            <ActionBanner
                                type="warning"
                                title="Session Expiring"
                                message="You have been inactive for a while. You will be logged out in less than a minute."
                                actionLabel="I'm here"
                                onAction={() => window.location.reload()} // Simple activity trigger
                                onDismiss={() => setShowInactivityWarning(false)}
                            />
                        </div>
                    </div>
                )}

                <ShortcutsHelpModal
                    isOpen={isShortcutsHelpOpen}
                    onClose={() => setIsShortcutsHelpOpen(false)}
                    shortcuts={shortcutsList}
                />

                <ErrorBoundary
                    FallbackComponent={GlobalErrorFallback}
                    onError={(error, info) => {
                        // Optional: send to Sentry, LogRocket, etc.
                        console.error("Global Error:", error, info);
                    }}
                >
                    <BrowserRouter>
                        <div id="main-content">
                            <Suspense fallback={
                                <div className="flex items-center justify-center min-h-screen">
                                    <Spinner message="Loading..." />
                                </div>
                            }>
                                <Routes>
                                    <Route path="/" element={<Home />} />
                                    <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
                                    <Route path="/signup" element={<PublicRoute><Signup /></PublicRoute>} />
                                    <Route path="/verify-account" element={<PublicRoute><Signup /></PublicRoute>} />
                                    <Route path="/account/*" element={<ProtectedRoute><AccountAccessRouter /></ProtectedRoute>} />
                                    <Route path="/account/delete" element={<ProtectedRoute><DeleteAccount /></ProtectedRoute>} />
                                    <Route path="/account/deactivate" element={<ProtectedRoute><DeactivateAccount /></ProtectedRoute>} />
                                    <Route path="/reset-password/*" element={<PublicRoute><Reset /></PublicRoute>} />
                                    <Route path="/link-account" element={<ProtectedRoute><LinkAccount /></ProtectedRoute>} />
                                    <Route path="/redirect/after-login" element={<RedirectAfterLogin />} />
                                    <Route path="/redirect/on-login" element={<RedirectOnLogin />} />
                                    <Route path="*" element={<Navigate to="/login" replace />} />
                                </Routes>
                            </Suspense>
                        </div>
                    </BrowserRouter>
                </ErrorBoundary>
            </InactivityProvider>
        </ApiClientInitializer>
    )
}

export default App
